import React, { useCallback, useState } from 'react'
import { apiGet } from '../generic/Api_Functions'
import AlertUI from '../generic/AlertUI'
import { Box, Button, Grid, TextareaAutosize, Typography } from '@mui/material'
import SmartTextField from '../generic/smarttextfield/SmartTextField'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { SmartSelect } from '../generic/smartSelect'
import { systemValueCodeLabel } from '../generic/systemValues/systemValueLabel'
import { QUALITY_INCIDENCE_PRIORITY_LABEL_KEYS } from '../Mos/Widgets/qualityIncidences/qualityIncidencePriority'
import { DateTimePicker } from '@mui/x-date-pickers'
import { getQualityIncidenceRowId } from './QualityIncidenceDeleteUtils'
import { PendingActionsOutlined, TextSnippetOutlined } from '@mui/icons-material'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import Document from '../configuration/document/Document'
import QualityIncidenceActionsTable from './QualityIncidenceActionsTable'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'
import ScopesSection from '../Mos/Widgets/qualityIncidences/components/ScopesSection'

const normalizeEntityId = (value) => {
  const normalized = String(value ?? '').trim()
  if (!normalized || normalized.toLowerCase() === 'null' || normalized.toLowerCase() === 'undefined') return null
  const parsed = Number.parseInt(normalized, 10)
  return String(parsed) === normalized ? parsed : normalized
}

const getEntityDisplayName = (value) => {
  if (!value) return ''
  if (typeof value === 'string') return value
  return String(value.name ?? value.code ?? value.fabricationOrder ?? value.id ?? '').trim()
}

const MOS_STATUS_OPTIONS = ['DRAFT', 'PENDING']

const normalizeStatus = (status) => String(status ?? '').trim().toUpperCase()

const buildResourceLabel = (resource) => {
  const code = String(resource?.resourceCode ?? resource?.code ?? '').trim()
  const name = String(resource?.resourceName ?? resource?.name ?? '').trim()
  return [code, name].filter(Boolean).join(' - ')
}

const parseAffectedUnits = (value, fallbackValue = null) => {
  const normalized = String(value || '').trim()
  if (!normalized) return fallbackValue
  const parsed = Number.parseInt(normalized, 10)
  if (Number.isNaN(parsed) || parsed < 1) return fallbackValue
  return parsed
}

const getAffectedUnits = (row, fallbackValue = 1) => (
  parseAffectedUnits(row?.affectedUnits ?? row?.affected, fallbackValue)
)

const sumAffectedUnits = (rows) => {
  const affectedRows = Array.isArray(rows) ? rows : []
  if (affectedRows.length === 0) return ''
  return String(affectedRows.reduce((total, row) => total + getAffectedUnits(row, 1), 0))
}

const buildAffectedUiRows = (scopeAffected, job = null) => {
  const items = Array.isArray(scopeAffected) ? scopeAffected : []
  const jobId = job?.id ?? null
  const jobName = String(job?.name ?? '').trim()
  return items
    .map((item, affectedIndex) => {
      const resource = item?.qualityIncidenceAffectedRawMaterialDto || item?.resource || item?.rawMaterial || item?.material || null
      const resourceId = item?.resourceId ?? resource?.resourceId ?? resource?.id ?? null
      if (resourceId == null) return null
      const resourceCode = String(item?.resourceCode ?? resource?.resourceCode ?? resource?.code ?? '').trim()
      const resourceName = String(item?.resourceName ?? resource?.resourceName ?? resource?.name ?? '').trim()
      const base = [resourceCode, resourceName].filter(Boolean).join(' - ')
      return {
        affectedRowId: String(item?.uuid || `${jobId ?? 'nojob'}-${resourceId}-${affectedIndex}`),
        uuid: item?.uuid ?? null,
        hasActions: Boolean(item?.hasActions),
        resourceId,
        jobId,
        jobName,
        resourceCode,
        resourceName,
        label: jobName ? `${jobName} - ${base}` : (base || buildResourceLabel(item?.qualityIncidenceAffectedRawMaterialDto || item)),
        affected: String(getAffectedUnits(item, 1))
      }
    })
    .filter(Boolean)
}

// The backend stores one scope per (OF, job); a multi-job UI row was exploded on save into N
// backend scopes, each with its own affecteds. To rebuild the row we group by FO and tag every
// resource with the job it came from, so the operator sees in one place "job1 - X", "job2 - Y".
const normalizeScopeRows = (scopes) => {
  const rows = Array.isArray(scopes) ? scopes : []
  const validRows = rows.filter((row) => row?.fabricationOrder?.id != null)

  const byFo = new Map()
  validRows.forEach((row) => {
    const foId = row.fabricationOrder.id
    if (!byFo.has(foId)) byFo.set(foId, [])
    byFo.get(foId).push(row)
  })

  const merged = []
  byFo.forEach((scopesInFo, foId) => {
    const jobs = []
    const aggregatedResources = []
    let primaryUuid = null
    let primaryRow = null
    let fabricationOrderCode = ''
    let endDate = null
    let hasActions = false
    const originalScopeUuids = []

    scopesInFo.forEach((scope) => {
      const nodeId = scope?.node?.id ?? scope?.node?.uuid ?? null
      const nodeName = getEntityDisplayName(scope?.node)
      const job = (nodeId != null || nodeName) ? { id: nodeId, name: nodeName } : null
      const scopeUuid = scope?.id ?? scope?.uuid ?? null
      const scopeAffected = Array.isArray(scope?.affected) ? scope.affected : []
      const affectedUiRows = buildAffectedUiRows(scopeAffected, job)
      // A job is "locked" if its own scope has actions or any of its affected resources does.
      // The multi-job multi-select uses this to prevent the operator from dropping a job that
      // would orphan an existing action.
      const scopeOrAffectedHasActions = Boolean(scope?.hasActions) ||
        affectedUiRows.some((row) => row?.hasActions)

      if (job) jobs.push({ ...job, scopeUuid, hasActions: scopeOrAffectedHasActions })
      if (scopeUuid) originalScopeUuids.push(scopeUuid)
      if (!primaryRow) {
        primaryRow = scope
        primaryUuid = scopeUuid
        fabricationOrderCode = getEntityDisplayName(scope?.fabricationOrder)
      }
      if (!endDate && scope?.endDate) endDate = scope.endDate
      if (scope?.hasActions) hasActions = true

      aggregatedResources.push(...affectedUiRows)
    })

    merged.push({
      scopeRowId: String(primaryUuid || `${foId ?? 'scope'}-${merged.length}`),
      uuid: primaryUuid,
      hasActions,
      fabricationOrderId: primaryRow?.fabricationOrder?.id ?? null,
      fabricationOrderCode,
      jobs,
      resource: aggregatedResources,
      affectedRows: aggregatedResources,
      affected: sumAffectedUnits(aggregatedResources),
      endDate,
      // Snapshot of scope uuids merged into this row at load time so we can mark scopes for
      // deletion when the user drops a job from the multi-select.
      originalScopeUuids
    })
  })

  return merged
}

const buildJobAffectedPayload = (resources, endDate) => {
  return (Array.isArray(resources) ? resources : [])
    .map((resource) => {
      const resourceId = normalizeEntityId(resource?.resourceId ?? resource?.id)
      if (resourceId == null) return null
      return {
        uuid: resource?.uuid || null,
        type: 'RAW_MATERIAL',
        resourceId,
        endDate,
        affectedUnits: parseAffectedUnits(resource?.affected, 1),
        hasActions: Boolean(resource?.hasActions)
      }
    })
    .filter(Boolean)
}

// Explode the UI row into one backend scope per job, and route each tagged resource to its
// matching job-scope by jobId. Resources tagged with an unknown job (legacy data, or jobless
// scope) go to the first scope as a fallback.
const buildScopePayload = (rows) => {
  return (Array.isArray(rows) ? rows : []).flatMap((row) => {
    const fabricationOrderId = normalizeEntityId(row?.fabricationOrderId)
    if (fabricationOrderId == null) return []
    const allResources = Array.isArray(row?.resource) ? row.resource : []
    const jobs = Array.isArray(row?.jobs) && row.jobs.length > 0
      ? row.jobs
      : (row?.job ? [row.job] : [null])
    const jobIdSet = new Set(jobs.map((job) => String(job?.id)).filter((v) => v !== 'null' && v !== ''))
    return jobs.map((job, index) => {
      const nodeId = normalizeEntityId(job?.id)
      const scopeUuid = job?.scopeUuid || (index === 0 ? (row?.uuid || null) : null)
      const matchingResources = allResources.filter((resource) => {
        if (resource?.jobId != null && String(resource.jobId) === String(job?.id)) return true
        // Untagged resources fall back to the first job so legacy rows still work.
        if (index === 0 && (resource?.jobId == null || !jobIdSet.has(String(resource.jobId)))) return true
        return false
      })
      return {
        id: scopeUuid,
        fabricationOrder: { id: fabricationOrderId },
        endDate: row?.endDate || null,
        node: nodeId == null ? null : { id: nodeId },
        affected: buildJobAffectedPayload(matchingResources, row?.endDate || null),
        hasActions: Boolean(row?.hasActions)
      }
    })
  })
}

const rowHasActions = (row) => Boolean(row?.hasActions)

const scopeRowHasActions = (row) => (
  rowHasActions(row) ||
  (Array.isArray(row?.resource) && row.resource.some(rowHasActions)) ||
  (Array.isArray(row?.affectedRows) && row.affectedRows.some(rowHasActions))
)

const buildHasActionsByUuid = (rows) => {
  const map = new Map()
  ;(Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row?.uuid) map.set(row.uuid, Boolean(row.hasActions))
  })
  return map
}

const applyHasActionsByUuid = (rows, hasActionsByUuid) => {
  return (Array.isArray(rows) ? rows : []).map((row) => (
    row?.uuid && hasActionsByUuid.has(row.uuid)
      ? { ...row, hasActions: hasActionsByUuid.get(row.uuid) }
      : row
  ))
}

const isSameScopeAffectedRow = (previousRow, nextRow) => {
  if (previousRow?.uuid && nextRow?.uuid) return previousRow.uuid === nextRow.uuid
  return previousRow?.resourceId != null && nextRow?.resourceId != null && String(previousRow.resourceId) === String(nextRow.resourceId)
}

/**
 * @param {Object} props Props object
 * @param {String} props.qualityIncidenceUuid Quality incidence uuid to edit, if null it will be a creation form
 * @param {Function} props.onSubmit Function that will be called when submitting the form, it will receive the form data as parameter
 * @param {'DEFAULT'|'MOS'} props.entryMode Mode of the form, DEFAULT or MOS
 */
const QualityIncidenceForm = React.forwardRef(({ qualityIncidenceUuid = null, onSubmit, entryMode = 'DEFAULT' }, ref) => {
  const { t_ } = useSmartTranslation()
  const { format } = useDateFormatter()
  const [data, setData] = useState(null)
  const [scopeRows, setScopeRows] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [priorityOptions, setPriorityOptions] = useState([])
  const [showActions, setShowActions] = useState(false)
  const [showDocument, setShowDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [pendingDeletes, setPendingDeletes] = useState({
    scopeIds: [],
    scopeAffectedIds: []
  })
  const [AlertElement, showAlert] = AlertUI()
  const [pendingActionsConfirmOpen, setPendingActionsConfirmOpen] = useState(false)
  const pendingActionsResolverRef = React.useRef(null)
  const isUserAuthorized = useAuthorization()

  const editMode = entryMode === 'DEFAULT' || entryMode === 'MOS'
  const editModeDefaultOnly = entryMode === 'DEFAULT'
  const showActionsButton = entryMode === 'DEFAULT'

  const fetchQualityIncidenceData = useCallback(async () => {
    if (!qualityIncidenceUuid) return null
    const response = await apiGet('getQualityIncidenceByUuid/' + qualityIncidenceUuid)
    setData(response)
    setScopeRows(normalizeScopeRows(response?.scopes))
    return response
  }, [qualityIncidenceUuid])

  const refreshActionsState = useCallback(async () => {
    if (!qualityIncidenceUuid) return
    const response = await apiGet('getQualityIncidenceByUuid/' + qualityIncidenceUuid)
    const nextScopeRows = normalizeScopeRows(response?.scopes)
    const scopeHasActionsByUuid = buildHasActionsByUuid(nextScopeRows)
    const scopeAffectedHasActionsByUuid = buildHasActionsByUuid(nextScopeRows.flatMap((row) => row.affectedRows || row.resource || []))

    setScopeRows((previousRows) => previousRows.map((row) => {
      const resource = applyHasActionsByUuid(row.resource, scopeAffectedHasActionsByUuid)
      const affectedRows = applyHasActionsByUuid(row.affectedRows, scopeAffectedHasActionsByUuid)

      return {
        ...row,
        hasActions: row?.uuid && scopeHasActionsByUuid.has(row.uuid)
          ? scopeHasActionsByUuid.get(row.uuid)
          : row.hasActions,
        resource,
        affectedRows
      }
    }))
  }, [qualityIncidenceUuid])

  React.useEffect(() => {
    const getStatusOptions = async () => {
      await apiGet('getQualityIncidenceStatus').then(response =>
        setStatusOptions(response)
      )
    }
    const getPriorityOptions = async () => {
      await apiGet('getQualityIncidencePriority').then(response =>
        setPriorityOptions(response)
      )
    }

    if (qualityIncidenceUuid) {
      setPendingDeletes({
        scopeIds: [],
        scopeAffectedIds: []
      })
      setScopeRows([])
      getStatusOptions()
      getPriorityOptions()
      fetchQualityIncidenceData()
    }
  }, [fetchQualityIncidenceData, qualityIncidenceUuid])

  const checkIfIsNotEmpty = useCallback((value, fieldName) => {
    if (!value) {
      return `${t_(fieldName)} ${t_('Can not be empty').toLocaleLowerCase()}`
    }
    return ''
  }, [t_])

  const validateForm = async () => {
    if (!data) return false
    const titleError = checkIfIsNotEmpty(data.title, 'Title')
    if (titleError) {
      showAlert({ title: titleError, severity: 'error' })
      return false
    }
    const descriptionError = checkIfIsNotEmpty(data.description, 'Description')
    if (descriptionError) {
      showAlert({ title: descriptionError, severity: 'error' })
      return false
    }
    // Each scope must reference at least one job — the backend stores one scope per (OF, job)
    // pair, so a scope with no jobs would either be skipped or saved as an orphan with node=null,
    // which is no longer a valid shape in the new model.
    const scopeWithoutJob = (Array.isArray(scopeRows) ? scopeRows : []).find((row) => (
      !Array.isArray(row?.jobs) || row.jobs.length === 0
    ))
    if (scopeWithoutJob) {
      showAlert({
        title: `${t_('Scope')} ${scopeWithoutJob.fabricationOrderCode || ''} ${t_('must have at least one job')}`.trim(),
        severity: 'error'
      })
      return false
    }
    return true
  }

  const addPendingDelete = useCallback((key, rows) => {
    const idsToDelete = (Array.isArray(rows) ? rows : [])
      .map((row) => getQualityIncidenceRowId(row))
      .filter((id) => id !== undefined && id !== null)

    if (idsToDelete.length === 0) return

    setPendingDeletes((prev) => ({
      ...prev,
      [key]: Array.from(new Set([...(prev[key] || []), ...idsToDelete]))
    }))
  }, [])

  const showDeleteBlockedAlert = useCallback(() => {
    showAlert({ title: t_('Can not delete because it has actions'), severity: 'error' })
  }, [showAlert, t_])

  const handleScopeRowsChange = useCallback((nextRows) => {
    const normalizedNextRows = Array.isArray(nextRows) ? nextRows : []
    const nextRowsById = new Map(normalizedNextRows.map((row) => [row.scopeRowId, row]))
    const nextRowIds = new Set(normalizedNextRows.map((row) => row.scopeRowId))
    const removedScopeRows = scopeRows.filter((row) => !nextRowIds.has(row.scopeRowId))
    const removedScopeAffected = []

    if (removedScopeRows.some(scopeRowHasActions)) {
      showDeleteBlockedAlert()
      return
    }

    scopeRows.forEach((previousRow) => {
      const nextRow = nextRowsById.get(previousRow.scopeRowId)
      if (!nextRow) return
      // Job ids that survived this change. Resources belonging to a job that is being dropped
      // are NOT tracked here — the backend cascades their delete when the parent scope is
      // removed, and sending the same uuid twice raises a "not found" error on the second pass.
      const nextJobIds = new Set((Array.isArray(nextRow.jobs) ? nextRow.jobs : [])
        .map((job) => String(job?.id))
        .filter((value) => value && value !== 'null'))
      ;(previousRow.affectedRows || previousRow.resource || [])
        .filter((previousAffectedRow) => {
          if (!previousAffectedRow?.uuid) return false
          if (previousAffectedRow.jobId != null && !nextJobIds.has(String(previousAffectedRow.jobId))) return false
          return !(nextRow.resource || nextRow.affectedRows || []).some((nextAffectedRow) => isSameScopeAffectedRow(previousAffectedRow, nextAffectedRow))
        })
        .forEach((row) => removedScopeAffected.push(row))
    })

    if (removedScopeAffected.some((row) => row?.hasActions)) {
      showDeleteBlockedAlert()
      return
    }

    if (removedScopeRows.length > 0) {
      // A UI scope row may wrap N backend scopes (one per job). The primary uuid (row.uuid)
      // is just the first; the rest live in originalScopeUuids. Expand to every backend uuid
      // so the entire scope set goes into scopedUUUIDToDelete instead of just the first one.
      const allRemovedScopeUuids = removedScopeRows.flatMap((row) => {
        const originalUuids = Array.isArray(row?.originalScopeUuids) ? row.originalScopeUuids : []
        if (originalUuids.length > 0) return originalUuids
        const fallbackId = row?.uuid ?? row?.id ?? null
        return fallbackId ? [fallbackId] : []
      })
      addPendingDelete('scopeIds', allRemovedScopeUuids.map((uuid) => ({ id: uuid })))
    }
    if (removedScopeAffected.length > 0) {
      addPendingDelete('scopeAffectedIds', removedScopeAffected)
    }
    setScopeRows(normalizedNextRows)
  }, [addPendingDelete, scopeRows, showDeleteBlockedAlert])

  const buildSubmitPayload = (closePendingActions) => {
    // When the user drops jobs from a merged multi-job scope row via the multi-select, the backing
    // scopes for the removed nodes must also be deleted; otherwise they would remain as orphans
    // pointing at jobs that no longer belong to the user's intent.
    const orphanedScopeIds = []
    ;(Array.isArray(scopeRows) ? scopeRows : []).forEach((row) => {
      const originalUuids = Array.isArray(row?.originalScopeUuids) ? row.originalScopeUuids : []
      if (originalUuids.length === 0) return
      const currentUuids = new Set((Array.isArray(row?.jobs) ? row.jobs : [])
        .map((job) => job?.scopeUuid)
        .filter(Boolean))
      originalUuids.forEach((uuid) => {
        if (!currentUuids.has(uuid)) orphanedScopeIds.push(uuid)
      })
    })

    const scopedUUUIDToDelete = Array.from(new Set([
      ...(pendingDeletes.scopeIds || []),
      ...orphanedScopeIds
    ]))

    return {
      ...data,
      affected: [],
      scopes: buildScopePayload(scopeRows),
      affectedUUUIDToDelete: [],
      scopedUUUIDToDelete,
      scopeAffectedUUUIDToDelete: pendingDeletes.scopeAffectedIds || [],
      ...(closePendingActions ? { closePendingActions: true } : {})
    }
  }

  // Opens the pending-actions confirmation dialog and resolves to the user's choice (accept/cancel).
  const askPendingActionsConfirmation = () => new Promise((resolve) => {
    pendingActionsResolverRef.current = resolve
    setPendingActionsConfirmOpen(true)
  })

  const resolvePendingActionsConfirmation = (confirmed) => {
    setPendingActionsConfirmOpen(false)
    const resolve = pendingActionsResolverRef.current
    pendingActionsResolverRef.current = null
    if (resolve) resolve(confirmed)
  }

  const handleSubmit = async () => {
    const valid = await validateForm()
    if (!valid) return false
    try {
      // Closing the incidence manually while it still has pending actions requires confirmation: ask the
      // user and, if accepted, send the flag so the backend closes those actions along with the incidence.
      if (normalizeStatus(data?.status) === 'CLOSED' && data?.uuid) {
        const hasPendingActions = await apiGet(`qualityIncidence/hasPendingActions/${data.uuid}`)
        if (hasPendingActions) {
          const confirmed = await askPendingActionsConfirmation()
          if (!confirmed) return false
          await onSubmit(buildSubmitPayload(true))
          return true
        }
      }
      await onSubmit(buildSubmitPayload(false))
      return true
    } catch (e) {
      showAlert({ title: e?.message || t_('Error updating quality incidence'), severity: 'error' })
      return false
    }
  }

  React.useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }))

  const filteredStatusOptions = React.useMemo(() => {
    if (entryMode === 'MOS') {
      return statusOptions.filter((option) => MOS_STATUS_OPTIONS.includes(normalizeStatus(option)))
    }
    // Quality (DEFAULT): every status except DRAFT, which is a MOS-only draft state.
    return statusOptions.filter((option) => normalizeStatus(option) !== 'DRAFT')
  }, [entryMode, statusOptions])
  return (
    <>
      <SmartDialog
        setOpen={pendingActionsConfirmOpen}
        title={{ icon: <PendingActionsOutlined />, render: <Typography variant='h4'>{t_('Pending actions')}</Typography> }}
        message={t_('This incidence still has pending actions. Do you want to close them and close the incidence?')}
        acceptLabel='CLOSE ACTIONS AND INCIDENCE'
        acceptCallback={() => resolvePendingActionsConfirmation(true)}
        cancelCallback={() => resolvePendingActionsConfirmation(false)}
        closeCallback={() => resolvePendingActionsConfirmation(false)}
      />
      <SmartDialog
        setOpen={showDocument}
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <Document
            reference={{ id: data?.uuid, code: data?.code }}
            resourceType='QUALITY_INCIDENCE'
            isDownloadingFile={isDownloadingFile}
            setIsDownloadingFile={setIsDownloadingFile}
            defaultName={data?.title}
          />
        }
      />
      <SmartDialog
        setOpen={showActions}
        closeCallback={() => setShowActions(false)}
        accept={false}
        cancel={false}
        close
        renderComponent={
          <QualityIncidenceActionsTable
            endpointType='qualityIncidence'
            formType='qualityIncidence'
            row={data}
            tableKey='qualityIncidenceActions'
            onActionsChanged={refreshActionsState}
          />
        }
      />
      <Grid container spacing={2} marginTop='0.5rem' marginBottom='0.5rem' sx={{ height: '100%', overflow: 'auto' }}>
        {/* Sticky inside the scroll container so the alert stays visible even when the operator
            is editing a scope further down the form. */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: theme => theme.zIndex.appBar, width: '100%' }}>
          {AlertElement}
        </Box>
        <Box component='legend' sx={{ ml: 0, mb: 1, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          {showActionsButton && (
            <Button
              sx={{ borderColor: 'transparent' }}
              color='primary'
              variant='outlined'
              onClick={() => setShowActions(true)}
            >
              <PendingActionsOutlined sx={{ fontSize: '1.2em' }} />
              <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('ACTIONS')}</Typography>
            </Button>
          )}

          <Button
            sx={{ borderColor: 'transparent' }}
            color='primary'
            variant='outlined'
            onClick={() => setShowDocument(true)}
            disabled={!isUserAuthorized('DOCUMENTS_QUALITY_INCIDENCE')}
          >
            <TextSnippetOutlined sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('DOCUMENTS')}</Typography>
          </Button>
        </Box>
        <Grid item xs={12} sm={4}>
          <SmartTextField
            label={t_('Title')}
            value={data?.title}
            style={{ width: '100%' }}
            required
            inputProps={{ maxLength: 120 }}
            onChange={(value) => setData({ ...data, title: value })}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartTextField
            label={t_('Code')}
            value={data?.code}
            style={{ width: '100%' }}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartSelect
            label={t_('Status')}
            value={filteredStatusOptions.find(opt => opt === data?.status)}
            selectableOptions={filteredStatusOptions}
            optionLabelTransform={(status) => systemValueCodeLabel(t_, 'QualityIncidenceStatusEnum', status)}
            onChange={(label) => {
              setData({ ...data, status: label })
            }}
            style={{ width: '100%' }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartSelect
            label={t_('Priority')}
            value={priorityOptions.find(opt => opt === data?.priority)}
            selectableOptions={priorityOptions}
            optionLabelTransform={(priority) => systemValueCodeLabel(t_, 'QualityIncidencePriorityEnum', priority, QUALITY_INCIDENCE_PRIORITY_LABEL_KEYS)}
            onChange={(label) => {
              setData({ ...data, priority: label })
            }}
            style={{ width: '100%' }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartTextField
            label={t_('Fabrication order')}
            value={data?.fabricationOrder?.name}
            style={{ width: '100%' }}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartTextField
            label={t_('Job')}
            value={getEntityDisplayName(data?.node)}
            style={{ width: '100%' }}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartTextField
            label={t_('Device')}
            value={data?.device?.name}
            style={{ width: '100%' }}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartTextField
            label={t_('Worker')}
            value={data?.worker ? data.worker.name + ' ' + data.worker.surname : null}
            style={{ width: '100%' }}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SmartTextField
            label={t_('Responsible')}
            value={data?.responsibleName || null}
            style={{ width: '100%' }}
            onChange={(event) => {
              setData({ ...data, responsibleName: event })
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <DateTimePicker
            label={t_('Created date')}
            value={data?.createdDate ? new Date(data.createdDate) : null}
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={10} />
        <Grid item xs={12} sm={12}>
          <Box>
            <Box component='legend' sx={{ px: 1, ml: 0, mb: 1, textAlign: 'left' }}>
              {t_('Description')}
            </Box>
            <TextareaAutosize
              placeholder={t_('Description')}
              value={data?.description || ''}
              onChange={(e) => setData({ ...data, description: e.target.value })}
              minRows={5}
              style={{ width: '100%', background: 'transparent' }}
              disabled={!editMode}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Box>
            <Box component='legend' sx={{ px: 1, ml: 0, mb: 1, textAlign: 'left' }}>
              {t_('Preliminary analysis')}
            </Box>
            <TextareaAutosize
              placeholder={t_('Preliminary analysis')}
              value={data?.preliminaryAnalysis || ''}
              onChange={(e) => setData({ ...data, preliminaryAnalysis: e.target.value })}
              minRows={5}
              style={{ width: '100%', background: 'transparent' }}
              disabled={!editMode}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Box>
            <Box component='legend' sx={{ px: 1, ml: 0, mb: 1, textAlign: 'left' }}>
              {t_('Proposed solution')}
            </Box>
            <TextareaAutosize
              placeholder={t_('Proposed solution')}
              value={data?.proposedSolution || ''}
              onChange={(e) => setData({ ...data, proposedSolution: e.target.value })}
              minRows={5}
              style={{ width: '100%', background: 'transparent' }}
              disabled={!editMode}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Box>
            <Box component='legend' sx={{ px: 1, ml: 0, mb: 1, textAlign: 'left' }}>
              {t_('Assessments')}
            </Box>
            <TextareaAutosize
              placeholder={t_('Assessment')}
              value={data?.assessment || ''}
              onChange={(e) => setData({ ...data, assessment: e.target.value })}
              minRows={5}
              style={{ width: '100%', background: 'transparent' }}
              disabled={!editModeDefaultOnly}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Box>
            <Box component='legend' sx={{ px: 1, ml: 0, mb: 1, textAlign: 'left' }}>
              {t_('Scopes')}
            </Box>
            <ScopesSection
              t_={t_}
              format={format}
              scopeRows={scopeRows}
              onScopeRowsChange={handleScopeRowsChange}
              onScopeRowsDeleteBlocked={showDeleteBlockedAlert}
              disabled={!editMode}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  )
})

export default QualityIncidenceForm
